/**
 * Initialises the Fragments engine and wires render-on-demand.
 *
 * Since the Fragments 3.0 rewrite the engine renders ONLY when explicitly told
 * to. Miss any of the three wirings below and you get a black or frozen
 * viewport with no error — the most common failure mode with this API.
 *
 * The worker is self-hosted (copied by scripts/copy-wasm.mjs). We deliberately
 * do NOT call the static FragmentsManager.getWorker(), which fetches from
 * unpkg at runtime. See CODE_RULES.md §7.
 *
 * API verified against @thatopen/components 3.4.8 type definitions.
 */
import type * as THREE from "three";
import * as OBC from "@thatopen/components";
import { ASSETS } from "../core/config";
import { logger } from "../core/logger";
import type { ArchWorld } from "./world";

/** The camera types the Fragments renderer accepts. */
type RenderCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

export function initFragments(components: OBC.Components, world: ArchWorld): OBC.FragmentsManager {
  const fragments = components.get(OBC.FragmentsManager);
  fragments.init(ASSETS.FRAGMENTS_WORKER);

  // Redraws are fire-and-forget by design: the engine coalesces them, and a
  // failed frame must not reject into an event handler with nowhere to go.
  const redraw = (force = false): void => {
    fragments.core.update(force).catch((cause: unknown) => {
      logger.error("Fragments redraw failed", cause);
    });
  };

  // 1. Redraw whenever the camera settles.
  world.camera.controls.addEventListener("update", () => {
    redraw();
  });

  // 2. Re-point every model at the active camera when it changes (2D <-> 3D).
  world.onCameraChanged.add((camera: OBC.BaseCamera) => {
    const active = asRenderCamera(camera.three);
    if (active === null) return;
    for (const [, model] of fragments.list) {
      model.useCamera(active);
    }
    redraw(true);
  });

  // 3. Add newly loaded models to the scene and force one draw.
  fragments.list.onItemSet.add(({ value: model }) => {
    const active = asRenderCamera(world.camera.three);
    if (active === null) {
      logger.error("Active camera is neither perspective nor orthographic; model not shown.");
      return;
    }
    model.useCamera(active);
    world.scene.three.add(model.object);
    redraw(true);
  });

  applyZFightingWorkaround(fragments);
  return fragments;
}

/**
 * `BaseCamera.three` is typed as the THREE.Camera supertype, but the Fragments
 * renderer needs the concrete union. Narrow via three's own runtime flags
 * rather than asserting — see CODE_RULES.md §6.
 */
function asRenderCamera(camera: THREE.Camera): RenderCamera | null {
  if (isPerspective(camera)) return camera;
  if (isOrthographic(camera)) return camera;
  return null;
}

/**
 * three types these discriminators as the literal `true`, so the property's
 * presence is the narrowing signal. A bare `in` check only yields an
 * intersection type, hence the explicit predicates.
 */
function isPerspective(camera: THREE.Camera): camera is THREE.PerspectiveCamera {
  return "isPerspectiveCamera" in camera;
}

function isOrthographic(camera: THREE.Camera): camera is THREE.OrthographicCamera {
  return "isOrthographicCamera" in camera;
}

/**
 * Coplanar IFC surfaces (a slab and its finish, stacked walls) z-fight badly.
 * A per-material random polygon offset is the workaround ThatOpen use in their
 * own examples. LOD materials are excluded — offsetting those causes popping.
 */
function applyZFightingWorkaround(fragments: OBC.FragmentsManager): void {
  fragments.core.models.materials.list.onItemSet.add(({ value: material }) => {
    if ("isLodMaterial" in material) return;
    material.polygonOffset = true;
    material.polygonOffsetUnits = 1;
    material.polygonOffsetFactor = Math.random();
  });
}
